/**
 * Pairing a television, and the seat it keeps afterwards — phase 11c.
 *
 * # Why a television needs its own service at all
 *
 * Every other client in this project holds its seat in an `HttpOnly` cookie it
 * cannot read, which is the whole point: script never touches the credential.
 * A packaged television app cannot do that. Phase 11b measured why — its page is
 * loaded from a file, so every `/api` request is cross-origin, and a
 * `SameSite=Lax` cookie is not sent cross-site whatever CORS allows.
 *
 * So this is the one place in the frontend that **holds a device token in
 * storage and sends it**. That is a real weakening and it is worth being precise
 * about what it costs: on a television there is no other origin's script to
 * steal it, no third-party frames, and one app. The alternative was no
 * television at all.
 *
 * # The flow
 *
 * 1. {@link startPairing} — the app asks for a code and shows it.
 * 2. The host types those six characters into their phone, which is in the lobby
 *    and holds the party's cookie.
 * 3. {@link pollPairing} — the app collects a device token of its own.
 * 4. {@link rememberSeat} — kept, so the next boot is not a second pairing.
 *
 * The code is the *addressing* half and the poll token is the *secret* half.
 * That split is why a photograph of a television is not a seat in the party —
 * see `src/auth/pairing.rs`.
 */

import { apiPost } from "./http.js";

/** What a television shows while it waits. */
export interface Pairing {
  /** Six characters, for the screen. */
  code: string;
  /** The secret to poll with. **Never rendered.** */
  poll_token: string;
  /** How long the code is worth showing, in seconds. */
  expires_in: number;
}

/** Where a pairing has got to. */
export interface PairingProgress {
  /** `waiting` · `paired` · `gone`. */
  status: string;
  /** The television's own seat, on `paired` only. */
  device_token?: string;
  /** Which party, so the board can render its join code. */
  party_code?: string;
}

/** Ask for a code to show. Needs no credential — that is what it is for. */
export async function startPairing(): Promise<Pairing> {
  return apiPost<Pairing>("/display/pair", {});
}

/**
 * Ask whether anybody has typed the code yet.
 *
 * `gone` means the code expired or was already collected, and the answer to both
 * is the same: show a fresh one. It is not an error and is not rendered as one.
 */
export async function pollPairing(pollToken: string): Promise<PairingProgress> {
  return apiPost<PairingProgress>("/display/pair/poll", { poll_token: pollToken });
}

/** Where the television's seat lives between boots. */
const SEAT_KEY = "sonolume.display_seat";

/** What a paired television remembers about itself. */
export interface DisplaySeat {
  /** The device token, presented as a bearer header. */
  token: string;
  /** Which party it is showing. */
  code: string;
}

/**
 * The seat this television is holding, if it has been paired.
 *
 * Storage can be absent or refused (a private context, a locked-down runtime),
 * and the honest answer to that is "not paired" rather than a thrown error on a
 * screen nobody is standing at.
 */
export function rememberedSeat(): DisplaySeat | null {
  try {
    const raw = window.localStorage.getItem(SEAT_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as DisplaySeat).token === "string" &&
      typeof (parsed as DisplaySeat).code === "string"
    ) {
      return parsed as DisplaySeat;
    }
    return null;
  } catch {
    return null;
  }
}

/** Keep the seat, so switching the television on again is not a re-pairing. */
export function rememberSeat(seat: DisplaySeat): void {
  try {
    window.localStorage.setItem(SEAT_KEY, JSON.stringify(seat));
  } catch {
    // A television that cannot persist still works for this party; it will pair
    // again next time. Failing the connection over it would be worse.
  }
}

/**
 * Forget it — after the party closes, or when the token stops being accepted.
 *
 * The second case is the one that matters: a remembered seat for a party that
 * has ended would otherwise leave a television showing "can't reach the party"
 * forever, with no way back to a pairing code and nobody able to type one.
 */
export function forgetSeat(): void {
  try {
    window.localStorage.removeItem(SEAT_KEY);
  } catch {
    // Nothing to do, and nothing worth saying about it.
  }
}
